import React, { useEffect, useState } from 'react';
import {
  Table,
  Tag,
  Space,
  Button,
  Typography,
  Select,
  Modal,
  message,
  Descriptions,
  Input,
} from 'antd';
import dayjs from 'dayjs';
import request from '@/utils/request';

interface Row {
  id: number;
  amountCents: number;
  status: 'pending' | 'approved' | 'paid' | 'rejected';
  bankName: string;
  accountLast4: string;
  holderName: string;
  rejectionReason: string | null;
  balanceSnapshotCents: number;
  createdAt: string;
  processedAt: string | null;
  creatorEmail: string | null;
  creatorDisplayName: string | null;
}

const STATUS_LABEL: Record<Row['status'], { text: string; color: string }> = {
  pending: { text: '待审核', color: 'processing' },
  approved: { text: '已通过（待打款）', color: 'blue' },
  paid: { text: '已到账', color: 'success' },
  rejected: { text: '已驳回', color: 'error' },
};

const fmtCents = (v: number) => `¥${(v / 100).toFixed(2)}`;

const WithdrawalListPage: React.FC = () => {
  const [items, setItems] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [status, setStatus] = useState<string>('pending');

  const [rejectTarget, setRejectTarget] = useState<Row | null>(null);
  const [reason, setReason] = useState('');
  const [reveal, setReveal] = useState<{ row: Row; account: string } | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await request.get<{ items: Row[]; total: number }>('/admin/withdrawals', {
        params: { page, pageSize, status: status || undefined },
      });
      const data = res.data as unknown as { items: Row[]; total: number };
      setItems(data.items);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, status]);

  const approve = async (id: number) => {
    await request.post(`/admin/withdrawals/${id}/approve`);
    message.success('已通过');
    load();
  };

  const markPaid = async (id: number) => {
    await request.post(`/admin/withdrawals/${id}/mark-paid`);
    message.success('已标记到账');
    load();
  };

  const reject = async () => {
    if (!rejectTarget || !reason.trim()) {
      message.error('请填写驳回理由');
      return;
    }
    await request.post(`/admin/withdrawals/${rejectTarget.id}/reject`, { reason });
    message.success('已驳回');
    setRejectTarget(null);
    setReason('');
    load();
  };

  const revealAccount = async (row: Row) => {
    try {
      const res = await request.post<{ accountNumber: string }>(
        `/admin/withdrawals/${row.id}/reveal-account`,
      );
      const data = res.data as unknown as { accountNumber: string };
      setReveal({ row, account: data.accountNumber });
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    }
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space style={{ justifyContent: 'space-between', width: '100%' }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          创作者提现审核
        </Typography.Title>
        <Select
          style={{ width: 200 }}
          value={status}
          onChange={(v) => setStatus(v)}
          options={[
            { label: '全部', value: '' },
            { label: '待审核', value: 'pending' },
            { label: '已通过未打款', value: 'approved' },
            { label: '已到账', value: 'paid' },
            { label: '已驳回', value: 'rejected' },
          ]}
        />
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        dataSource={items}
        pagination={{
          current: page,
          pageSize,
          total,
          onChange: (p, s) => {
            setPage(p);
            setPageSize(s);
          },
        }}
        columns={[
          { title: 'ID', dataIndex: 'id', width: 70 },
          {
            title: '创作者',
            render: (_, r) => r.creatorDisplayName || r.creatorEmail || '-',
          },
          {
            title: '金额',
            dataIndex: 'amountCents',
            render: (v) => <Tag color="gold">{fmtCents(v)}</Tag>,
          },
          {
            title: '到账卡',
            render: (_, r) => `${r.bankName} **** ${r.accountLast4} (${r.holderName})`,
          },
          {
            title: '状态',
            dataIndex: 'status',
            render: (v) => {
              const l = STATUS_LABEL[v as Row['status']];
              return <Tag color={l?.color ?? 'default'}>{l?.text ?? v}</Tag>;
            },
          },
          {
            title: '申请时间',
            dataIndex: 'createdAt',
            render: (v) => dayjs(v).format('YYYY-MM-DD HH:mm'),
          },
          {
            title: '操作',
            render: (_, row) => (
              <Space>
                {row.status === 'pending' && (
                  <>
                    <Button size="small" type="link" onClick={() => approve(row.id)}>
                      通过
                    </Button>
                    <Button
                      size="small"
                      type="link"
                      danger
                      onClick={() => setRejectTarget(row)}
                    >
                      驳回
                    </Button>
                  </>
                )}
                {row.status === 'approved' && (
                  <>
                    <Button size="small" type="link" onClick={() => revealAccount(row)}>
                      查看账号
                    </Button>
                    <Button size="small" type="link" onClick={() => markPaid(row.id)}>
                      标记已到账
                    </Button>
                    <Button
                      size="small"
                      type="link"
                      danger
                      onClick={() => setRejectTarget(row)}
                    >
                      驳回
                    </Button>
                  </>
                )}
              </Space>
            ),
          },
        ]}
      />

      <Modal
        open={!!rejectTarget}
        title={`驳回提现 #${rejectTarget?.id}`}
        onOk={reject}
        onCancel={() => setRejectTarget(null)}
      >
        <Input.TextArea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="驳回理由，创作者会收到系统消息"
        />
      </Modal>

      <Modal
        open={!!reveal}
        title={`银行卡信息 (#${reveal?.row.id})`}
        onCancel={() => setReveal(null)}
        footer={null}
      >
        {reveal && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="持卡人">{reveal.row.holderName}</Descriptions.Item>
            <Descriptions.Item label="开户行">{reveal.row.bankName}</Descriptions.Item>
            <Descriptions.Item label="卡号">
              <Typography.Text copyable>{reveal.account}</Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label="金额">{fmtCents(reveal.row.amountCents)}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </Space>
  );
};

export default WithdrawalListPage;
